// client/src/components/BugReporter.tsx
//
// "Report a problem" button + modal, matched to swa-rprs's BugReporter: the 🐛
// button (.app-bugreport-btn) and a solid, centered, dark-teal-header modal —
// with the "Report a problem" / "My reports" tab strip, the captured
// technical-details disclosure (from utils/bugLog), and the KbBees success cheer.
// Rendered through a portal to document.body so it always centers over the viewport.
//
// Feeds the Dev Team board via the shared BugReports store. PREVIEW: local store
// today; "My reports" filters the store by the signed-in email, and the captured
// logs/events/context ride along for when the /api/bug-report backend lands.
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../auth/AuthContext';
import { useBugReports, BugReport, BugStatus } from '../contexts/BugReportsContext';
import { snapshotLogs, snapshotEvents, snapshotClientContext } from '../utils/bugLog';
import { submitReport, fetchMyReports } from '../services/devTeamApi';
import KbBees, { preloadKbBees } from './KbBees';
import './BugReporter.css';

const BUG_EMOJI = '\u{1F41B}'; // 🐛 — the exact glyph swa-rprs uses

// Palette + control styles copied from swa-rprs's BugReporter so the box matches.
const COLORS = {
  brandDark: '#003D4C', brandGreen: '#6CC24A', bg: '#ffffff',
  text: '#1f2937', muted: '#6b7280', border: '#e5e7eb', cardBg: '#f8fafc',
};
const textareaStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', resize: 'vertical',
  border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px',
  fontSize: 13.5, fontFamily: 'inherit', lineHeight: 1.45, color: '#1f2937',
};
const primaryBtn: React.CSSProperties = {
  padding: '10px 22px', background: COLORS.brandGreen, color: 'white',
  border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 700, cursor: 'pointer',
};
const secondaryBtn: React.CSSProperties = {
  padding: '10px 18px', background: 'white', color: COLORS.muted,
  border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const STATUS_LABELS: Record<BugStatus, { t: string; c: string; b: string }> = {
  inbox_triage: { t: 'Inbox Triage', c: '#5B21B6', b: '#EDE9FE' },
  open: { t: 'Open', c: '#92400E', b: '#FEF3C7' },
  in_progress: { t: 'In progress', c: '#3730A3', b: '#E0E7FF' },
  resolved: { t: 'Resolved', c: '#065F46', b: '#D1FAE5' },
};

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
        {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        flex: 1, padding: '10px 12px', background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 13, fontWeight: 700, color: active ? COLORS.brandDark : COLORS.muted,
        borderBottom: active ? `2px solid ${COLORS.brandGreen}` : '2px solid transparent',
      }}>
      {children}
    </button>
  );
}

// The signed-in user's own reports, read from the store (filtered by email).
function MyReportsPanel({ reports, onReport }: { reports: BugReport[]; onReport: () => void }) {
  return (
    <div style={{ padding: '18px 24px', overflowY: 'auto', fontSize: 14, minHeight: 160 }}>
      {reports.length === 0 ? (
        <div style={{ textAlign: 'center', color: COLORS.muted, padding: '28px 12px' }}>
          <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>{BUG_EMOJI}</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>You haven't reported any problems yet.</div>
          <button onClick={onReport} style={{ ...secondaryBtn, marginTop: 14 }}>Report a problem</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {reports.map(r => {
            const s = STATUS_LABELS[r.status];
            return (
              <div key={r.bugReportId} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '10px 12px', background: COLORS.cardBg }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ fontWeight: 600, color: COLORS.text, fontSize: 13.5, lineHeight: 1.3 }}>{r.issue}</div>
                  <span style={{ flexShrink: 0, padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: s.c, background: s.b, whiteSpace: 'nowrap' }}>{s.t}</span>
                </div>
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 3 }}>#{r.bugReportId} · {r.when}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function BugReporter() {
  const { email } = useAuth();
  const { reports, addReport } = useBugReports();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'report' | 'mine'>('report');
  const [issue, setIssue] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [showCaptured, setShowCaptured] = useState(false);
  const [done, setDone] = useState<number | null>(null); // reference # of the sent report

  // Warm the KB cheer frames when the modal opens so success plays instantly.
  useEffect(() => { if (open) preloadKbBees(); }, [open]);

  const reset = () => { setIssue(''); setDescription(''); setSteps(''); setShowCaptured(false); setDone(null); };
  const close = () => { setOpen(false); reset(); setTab('report'); };
  const openModal = () => { setOpen(true); setTab('report'); };

  // My reports: prefer the server; fall back to the local store on any error.
  const localMine = useMemo(
    () => reports.filter(r => (r.submittedBy || '').toLowerCase() === (email || '').toLowerCase()),
    [reports, email],
  );
  const [myReports, setMyReports] = useState<BugReport[] | null>(null);
  useEffect(() => {
    if (!open || tab !== 'mine') return;
    let cancelled = false;
    fetchMyReports()
      .then(rows => { if (!cancelled) setMyReports(rows); })
      .catch(() => { if (!cancelled) setMyReports(null); }); // null → render local fallback
    return () => { cancelled = true; };
  }, [open, tab]);

  const logCount = open ? snapshotLogs().length : 0;
  const eventCount = open ? snapshotEvents().length : 0;

  const canSubmit = issue.trim().length > 0;
  const submit = async () => {
    if (!canSubmit) return;
    const payload = {
      issue: issue.trim(),
      description: description.trim() || undefined,
      steps: steps.trim() || undefined,
      submittedBy: email || 'unknown',
      contextApp: 'admin-hq',
      logs: snapshotLogs(),
      events: snapshotEvents(),
      context: snapshotClientContext(),
    };
    try {
      const id = await submitReport(payload);
      setDone(id);
    } catch {
      // Backend not reachable yet → keep working against the local preview store.
      const created = addReport(payload);
      setDone(created.bugReportId);
    }
  };

  return (
    <>
      <button className="app-bugreport-btn" title="Report a problem" type="button" aria-label="Report a problem" onClick={openModal}>
        {BUG_EMOJI}
      </button>

      {open && createPortal(
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div style={{
            background: COLORS.bg, color: COLORS.text, borderRadius: 14,
            width: 'min(580px, 94vw)', maxHeight: '90vh', overflow: 'hidden',
            display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,.35)',
          }}>
            {/* Header */}
            <div style={{ background: COLORS.brandDark, color: 'white', padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 26, lineHeight: 1 }}>{BUG_EMOJI}</span>
              <div>
                <div style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.1 }}>Report a problem</div>
                <div style={{ fontSize: 13, color: '#cbe5d8', marginTop: 2 }}>
                  Tell us what went wrong — we'll attach the technical details automatically.
                </div>
              </div>
            </div>

            {/* Tab strip */}
            <div style={{ display: 'flex', borderBottom: `1px solid ${COLORS.border}`, background: '#fff' }}>
              <TabBtn active={tab === 'report'} onClick={() => setTab('report')}>Report a problem</TabBtn>
              <TabBtn active={tab === 'mine'} onClick={() => setTab('mine')}>My reports</TabBtn>
            </div>

            {tab === 'mine' ? (
              <MyReportsPanel reports={myReports ?? localMine} onReport={() => setTab('report')} />
            ) : done !== null ? (
              // Success state
              <div style={{ padding: '28px 24px', textAlign: 'center' }}>
                <KbBees size={132} fps={8} />
                <div style={{ fontSize: 17, fontWeight: 700, color: COLORS.brandDark }}>Thanks — report sent.</div>
                <p style={{ fontSize: 13.5, color: COLORS.muted, margin: '8px 0 0', lineHeight: 1.5 }}>
                  Reference <strong>#{done}</strong>. It's now on the Dev Team board, with the page you were on and
                  recent app activity attached.
                </p>
                <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <button onClick={close} style={primaryBtn}>Close</button>
                  <button onClick={reset} style={secondaryBtn}>Report another</button>
                </div>
              </div>
            ) : (
              <>
                {/* Body */}
                <div style={{ padding: '18px 24px', overflowY: 'auto', fontSize: 14 }}>
                  <Field label="Issue" required>
                    <textarea value={issue} onChange={e => setIssue(e.target.value)} placeholder="A short summary of the problem" rows={2} style={textareaStyle} autoFocus />
                  </Field>
                  <Field label="Description">
                    <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What happened, and any error you saw" rows={3} style={textareaStyle} />
                  </Field>
                  <Field label="Steps to Reproduce">
                    <textarea value={steps} onChange={e => setSteps(e.target.value)} placeholder={'1. …\n2. …\n3. …'} rows={3} style={textareaStyle} />
                  </Field>

                  {/* Captured-context disclosure */}
                  <button
                    type="button"
                    onClick={() => setShowCaptured(s => !s)}
                    style={{ background: 'none', border: 'none', padding: 0, marginTop: 4, color: COLORS.brandDark, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
                  >
                    {showCaptured ? '▾' : '▸'} We'll also include page &amp; technical details ({logCount} log lines, {eventCount} events)
                  </button>
                  {showCaptured && (
                    <ul style={{ margin: '8px 0 0', padding: '10px 10px 10px 26px', fontSize: 12.5, color: COLORS.muted, background: COLORS.cardBg, borderRadius: 8, lineHeight: 1.6 }}>
                      <li>Current page URL &amp; route</li>
                      <li>Your sign-in (email)</li>
                      <li>Browser, screen &amp; viewport size</li>
                      <li>Recent console logs and any errors captured this session</li>
                    </ul>
                  )}
                </div>

                {/* Footer */}
                <div style={{ padding: '14px 24px 18px', borderTop: `1px solid ${COLORS.border}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button onClick={close} style={secondaryBtn}>Cancel</button>
                  <button onClick={submit} disabled={!canSubmit}
                    style={{ ...primaryBtn, opacity: canSubmit ? 1 : 0.55, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
                    Send report
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
